import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Divider,
    Empty,
    Image,
    Input,
    List,
    message,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import ReactMarkdown from 'react-markdown';
import {
    AimOutlined,
    ApartmentOutlined,
    CommentOutlined,
    MessageOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    RobotOutlined,
    SendOutlined,
    SolutionOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';
import '../style/Assistant.css';
import PatternPointMapCard from '../components/PatternPointMapCard';
import { formatMeasurementTimestamp, formatUtcTimestamp } from '../utils/measurementTime';
import type { Geometry, MultiPolygon, Point, Polygon } from 'geojson';
import type { Dayjs } from 'dayjs';

const { Option } = Select;
const { Paragraph, Text, Title } = Typography;
const { RangePicker } = DatePicker;

interface CompanyMembership {
    membershipId: number;
    company: { id: number; name: string; logoUrl?: string | null };
    role: { id: number; code: string; description: string };
}

interface AreaTypeItem {
    id: number;
    name: string;
    description?: string;
    isGlobal?: boolean;
}

interface AreaItem {
    id: number;
    name: string;
    areaTypeId?: number | null;
    companyId?: number | null;
    geom?: Geometry | null;
}

interface PatternItem {
    id: number;
    name: string;
    patternType?: string;
    coordSystem?: string | null;
    areaId?: number;
    definition?: Record<string, any> | null;
}

interface PatternPointItem {
    id: number;
    label: string;
    pointIndex: number;
    xLocal?: number | null;
    yLocal?: number | null;
    gpsLat?: number | null;
    gpsLon?: number | null;
}

interface MeasurementValue {
    valueType: string;
    numValue?: number;
    textValue?: string;
    processingStage?: string | null;
}

interface MeasurementFile {
    id: number;
    fileName: string;
    fileType?: string;
    fileSize?: number;
    mimeType?: string;
    signedUrl?: string;
    processingStage?: string | null;
}

interface MeasurementItem {
    id: number;
    measurementCode: string;
    createdAt: string;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    areaId?: number | null;
    patternId?: number | null;
    patternPointId?: number | null;
    geom?: Point | null;
    device?: {
        id: number;
        label: string;
        serialNumber?: string;
        codePrefix?: string;
    } | null;
    values?: MeasurementValue[];
    files?: MeasurementFile[];
}

interface ConversationSummary {
    id: number;
    areaId?: number | null;
    measurementId?: number | null;
    patternPointId?: number | null;
    status: string;
    createdAt: string;
    updatedAt: string;
    area?: { id: number; name: string };
    patternPoint?: { id: number; name?: string };
    agentContext?: { id: number; name?: string } | null;
}

interface BackendMessage {
    id?: number;
    sender: string;
    content: string;
    messageType?: string;
    metadata?: any;
    createdAt?: string;
}

interface AssistantMessage {
    id: string;
    sender: 'agent' | 'user';
    content: string;
    messageType?: string;
    metadata?: any;
    createdAt?: string;
    extraMeta?: Record<string, any>;
}

interface ConversationInitialization {
    type: string;
    suggestedQueries?: string[];
}

const formatDate = (value?: string) => {
    if (!value) return '';
    try {
        const date = new Date(value);
        return date.toLocaleString();
    } catch {
        return value;
    }
};

const filterProcessed = <T extends { processingStage?: string | null }>(items?: T[]) =>
    (items || []).filter((item) => item.processingStage === 'processed');

const normalizeMeasurement = (measurement: MeasurementItem): MeasurementItem => ({
    ...measurement,
    values: filterProcessed(measurement.values),
    files: filterProcessed(measurement.files),
});

const getValueFromMeasurement = (measurement: MeasurementItem, type: string) => {
    const values = filterProcessed(measurement.values);
    if (!values.length) return null;
    const value = values.find((entry) => entry.valueType === type);
    if (!value) return null;
    return value.textValue || value.numValue;
};

const formatMeasurementValue = (value?: MeasurementValue) => {
    if (!value) return '';
    if (value.textValue) return value.textValue;
    if (typeof value.numValue === 'number') return value.numValue.toString();
    return '';
};

const formatFileSize = (size?: number) => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const extractImageUrls = (content?: string): string[] => {
    if (!content) return [];
    const urlRegex = /https?:\/\/[^\s)]+/g;
    const matches = content.match(urlRegex) || [];
    const imageUrls = matches.filter((raw) => {
        const cleaned = raw.replace(/[),.]+$/, '');
        const isImageExtension = /\.(png|jpg|jpeg|webp)(\?|$)/i.test(cleaned);
        const isMapboxStatic = cleaned.includes('api.mapbox.com/styles') && cleaned.includes('/static/');
        return isImageExtension || isMapboxStatic;
    });
    return Array.from(new Set(imageUrls.map((url) => url.replace(/[),.]+$/, ''))));
};

const buildAttachmentList = (messageItem: AssistantMessage): Array<{ url: string; label?: string }> => {
    const attachments = messageItem.metadata?.attachments || messageItem.metadata?.images || [];
    const list: Array<{ url: string; label?: string }> = Array.isArray(attachments)
        ? attachments
            .map((item: any) => ({
                url: item?.url || item?.accessUrl || item?.imageUrl,
                label: item?.label || item?.metricName || item?.name || item?.title
            }))
            .filter((item: any) => typeof item.url === 'string')
        : [];
    const contentUrls = extractImageUrls(messageItem.content);
    contentUrls.forEach((url) => {
        if (!list.find((item) => item.url === url)) {
            list.push({ url });
        }
    });
    return list;
};

const normalizeMessages = (rawMessages: BackendMessage[] = []): AssistantMessage[] =>
    rawMessages.map((msg) => ({
        id: `server-${msg.id ?? `${msg.messageType || 'msg'}-${Date.now()}`}`,
        sender: msg.sender === 'agent' ? 'agent' : 'user',
        content: msg.content,
        messageType: msg.messageType,
        metadata: msg.metadata,
        createdAt: msg.createdAt,
    }));

const sortMessages = (messages: AssistantMessage[]): AssistantMessage[] =>
    messages
        .map((message, index) => ({
            message,
            index,
            timestamp: message.createdAt ? new Date(message.createdAt).getTime() : Number.MAX_SAFE_INTEGER
        }))
        .sort((a, b) => (a.timestamp === b.timestamp ? a.index - b.index : a.timestamp - b.timestamp))
        .map((entry) => entry.message);

const mergeMessages = (
    currentMessages: AssistantMessage[],
    serverMessages: AssistantMessage[]
): AssistantMessage[] => {
    const serverIds = new Set(serverMessages.map((message) => message.id));
    const pendingLocal = currentMessages.filter(
        (message) => message.id.startsWith('local-') && !serverIds.has(message.id),
    );
    const dedupedPending = pendingLocal.filter(
        (local) => !serverMessages.some(
            (server) => server.sender === local.sender && server.content === local.content,
        ),
    );
    return sortMessages([...serverMessages, ...dedupedPending]);
};

const ConversationalAssistant: React.FC = () => {
    const { currentUser, loginUser } = useAuth();
    const [memberships, setMemberships] = useState<CompanyMembership[]>([]);
    const [membershipsLoading, setMembershipsLoading] = useState(false);
    const [companySwitching, setCompanySwitching] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(
        currentUser?.currentCompanyId || currentUser?.companyId,
    );

    const [areaTypes, setAreaTypes] = useState<AreaTypeItem[]>([]);
    const [areas, setAreas] = useState<AreaItem[]>([]);
    const [patterns, setPatterns] = useState<PatternItem[]>([]);
    const [patternPoints, setPatternPoints] = useState<PatternPointItem[]>([]);
    const [measurements, setMeasurements] = useState<MeasurementItem[]>([]);
    const [unassignedMeasurements, setUnassignedMeasurements] = useState<MeasurementItem[]>([]);
    const [conversationList, setConversationList] = useState<ConversationSummary[]>([]);

    const [selectedAreaTypeId, setSelectedAreaTypeId] = useState<number | null>(null);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [selectedPatternId, setSelectedPatternId] = useState<number | null>(null);
    const [selectedPatternPointId, setSelectedPatternPointId] = useState<number | null>(null);
    const [selectedMeasurementId, setSelectedMeasurementId] = useState<number | null>(null);
    const [areaRange, setAreaRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

    const [contextLoading, setContextLoading] = useState(false);
    const [measurementsLoading, setMeasurementsLoading] = useState(false);
    const [unassignedLoading, setUnassignedLoading] = useState(false);
    const [conversationsLoading, setConversationsLoading] = useState(false);

    const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
    const [conversationInitialization, setConversationInitialization] =
        useState<ConversationInitialization | null>(null);
    const pendingUserMessageRef = useRef<number | null>(null);
    const [messagesList, setMessagesList] = useState<AssistantMessage[]>([]);
    const [messageInput, setMessageInput] = useState('');
    const [chatBusy, setChatBusy] = useState(false);
    const [whatsAppContextLoading, setWhatsAppContextLoading] = useState(false);
    const [whatsAppContextModalOpen, setWhatsAppContextModalOpen] = useState(false);
    const [whatsAppContextLink, setWhatsAppContextLink] = useState<string | null>(null);
    const [whatsAppContextMessage, setWhatsAppContextMessage] = useState<string | null>(null);
    const [whatsAppContextExpiresAt, setWhatsAppContextExpiresAt] = useState<string | null>(null);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const pollingRef = useRef<number | null>(null);

    const fetchMemberships = useCallback(async () => {
        console.log('[Assistant] Fetching memberships...');
        setMembershipsLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.GET_MEMBERSHIPS, { withCredentials: true });
            const list: CompanyMembership[] = response.data?.memberships || [];
            console.log('[Assistant] Memberships loaded', list);
            setMemberships(list);
            setSelectedCompanyId((current) => current ?? list[0]?.company.id);
        } catch (error) {
            console.error('Unable to fetch memberships', error);
            message.error('Unable to load company memberships');
        } finally {
            setMembershipsLoading(false);
        }
    }, []);

    const handleCompanySwitch = async (companyId: number) => {
        if (!currentUser) return;
        console.log('[Assistant] Switching company to', companyId);
        setCompanySwitching(true);
        try {
            const response = await axios.post(
                ENDPOINTS.SWITCH_COMPANY,
                { companyId },
                { withCredentials: true },
            );

            const token = response.data?.accessToken;
            if (token) {
                localStorage.setItem('accessToken', token);
                localStorage.setItem('oscar_access_token', token);
                console.log('[Assistant] Stored new token from company switch');
            }

            loginUser({
                ...currentUser,
                currentCompanyId: companyId,
                currentCompanyRole: response.data?.roleInCurrentCompany,
            });

            setSelectedCompanyId(companyId);
            message.success(`Switched to ${response.data?.currentCompany?.name || 'selected company'}`);
            resetConversationContext();
        } catch (error) {
            console.error('Company switch failed', error);
            message.error('Could not switch company');
        } finally {
            setCompanySwitching(false);
        }
    };

    const resetConversationContext = () => {
        setSelectedAreaTypeId(null);
        setSelectedAreaId(null);
        setSelectedPatternId(null);
        setSelectedPatternPointId(null);
        setSelectedMeasurementId(null);
        setAreaTypes([]);
        setAreas([]);
        setPatterns([]);
        setPatternPoints([]);
        setMeasurements([]);
        setActiveConversationId(null);
        setMessagesList([]);
        setConversationInitialization(null);
        setWhatsAppContextModalOpen(false);
        setWhatsAppContextLink(null);
        setWhatsAppContextMessage(null);
        setWhatsAppContextExpiresAt(null);
    };

    const loadAreaTypes = useCallback(async () => {
        if (!selectedCompanyId) return;
        console.log('[Assistant] Loading area types for company', selectedCompanyId);
        setContextLoading(true);
        try {
            const [companyResponse, globalResponse] = await Promise.all([
                axios.get(ENDPOINTS.AREA_TYPES_V2, {
                    params: { company_id: selectedCompanyId },
                    withCredentials: true,
                }),
                axios.get(ENDPOINTS.AREA_TYPES_V2, {
                    params: { is_global: true },
                    withCredentials: true,
                }),
            ]);

            const companyTypes = companyResponse.data?.data || [];
            const globalTypes = globalResponse.data?.data || [];
            console.log('[Assistant] Area types loaded', { companyTypes, globalTypes });

            const map = new Map<number, AreaTypeItem>();
            [...globalTypes, ...companyTypes].forEach((type) => {
                if (type?.id !== undefined) {
                    map.set(type.id, type);
                }
            });

            setAreaTypes(Array.from(map.values()));
        } catch (error) {
            console.error('Error loading area types', error);
            message.error('Unable to load area types');
        } finally {
            setContextLoading(false);
        }
    }, [selectedCompanyId]);

    const loadAreas = useCallback(async (areaTypeId: number) => {
        console.log('[Assistant] Loading areas for areaType', areaTypeId);
        setContextLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.AREAS_V2, {
                params: { area_type_id: areaTypeId, limit: 200 },
                withCredentials: true,
            });
            setAreas(response.data?.data || []);
            console.log('[Assistant] Areas loaded', response.data?.data);
        } catch (error) {
            console.error('Error loading areas', error);
            message.error('Unable to load areas');
        } finally {
            setContextLoading(false);
        }
    }, []);

    const loadPatterns = useCallback(async (areaId: number) => {
        console.log('[Assistant] Loading patterns for area', areaId);
        setContextLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.AREA_PATTERNS(areaId), {
                params: { limit: 200 },
                withCredentials: true,
            });
            setPatterns(response.data?.data || response.data || []);
            console.log('[Assistant] Patterns loaded', response.data?.data || response.data);
        } catch (error) {
            console.error('Error loading patterns', error);
            message.error('Unable to load patterns');
        } finally {
            setContextLoading(false);
        }
    }, []);

    const loadPatternPoints = useCallback(async (patternId: number) => {
        console.log('[Assistant] Loading pattern points', patternId);
        setContextLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.PATTERN_POINTS(patternId), {
                withCredentials: true,
            });
            setPatternPoints(response.data?.data || response.data || []);
            console.log('[Assistant] Pattern points loaded', response.data?.data || response.data);
        } catch (error) {
            console.error('Error loading pattern points', error);
            message.error('Unable to load pattern points');
        } finally {
            setContextLoading(false);
        }
    }, []);

    const loadMeasurementsForPattern = useCallback(async (patternId: number) => {
        console.log('[Assistant] Loading measurements for pattern', patternId);
        setMeasurementsLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                params: { pattern_id: patternId, limit: 100 },
                withCredentials: true,
            });
            const payload: MeasurementItem[] = response.data?.data || [];
            setMeasurements(payload.map(normalizeMeasurement));
            console.log('[Assistant] Measurements loaded', response.data?.data);
        } catch (error) {
            console.error('Error loading measurements', error);
            message.error('Unable to load measurements');
        } finally {
            setMeasurementsLoading(false);
        }
    }, []);

    const loadUnassignedMeasurements = useCallback(async () => {
        console.log('[Assistant] Loading unassigned measurements...');
        setUnassignedLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.MEASUREMENTS_V2, {
                params: { limit: 100 },
                withCredentials: true,
            });
            const data: MeasurementItem[] = response.data?.data || [];
            const normalized = data.map(normalizeMeasurement);
            setUnassignedMeasurements(normalized.filter((item) => !item.patternPointId));
            console.log('[Assistant] Unassigned measurements loaded', data);
        } catch (error) {
            console.error('Error loading unassigned measurements', error);
            message.error('Unable to load unassigned measurements');
        } finally {
            setUnassignedLoading(false);
        }
    }, []);

    const loadConversations = useCallback(async () => {
        if (!selectedCompanyId) {
            setConversationList([]);
            return;
        }
        console.log('[Assistant] Loading conversations for company', selectedCompanyId);
        setConversationsLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.CONVERSATIONS, {
                params: { companyId: selectedCompanyId, limit: 25 },
                withCredentials: true,
            });
            setConversationList(response.data?.data || []);
            console.log('[Assistant] Conversations loaded', response.data?.data);
        } catch (error) {
            console.error('Error loading conversations', error);
            message.error('Unable to load conversations');
        } finally {
            setConversationsLoading(false);
        }
    }, [selectedCompanyId]);

    const hydrateConversation = (conversation: any, initialization?: ConversationInitialization | null) => {
        const rawMessages: BackendMessage[] = conversation?.messages || [];
        setActiveConversationId(conversation?.id || null);
        setMessagesList(normalizeMessages(rawMessages));
        setConversationInitialization(initialization || null);
    };

    const stopConversationPolling = useCallback(() => {
        if (pollingRef.current) {
            window.clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const fetchConversationDetail = useCallback(async (conversationId: number) => {
        try {
            const response = await axios.get(ENDPOINTS.CONVERSATION_DETAIL(conversationId), { withCredentials: true });
            const conversation = response.data?.data;
            if (conversation?.messages) {
                setMessagesList((prev) => mergeMessages(prev, normalizeMessages(conversation.messages)));
            }
            return conversation;
        } catch (error) {
            console.error('Unable to fetch conversation detail', error);
            return null;
        }
    }, []);

    const startConversationPolling = useCallback((conversationId: number) => {
        stopConversationPolling();
        setChatBusy(true);
        const startedAt = Date.now();

        const poll = async () => {
            const conversation = await fetchConversationDetail(conversationId);
            const messages: BackendMessage[] = conversation?.messages || [];
            const lastAgentMessage = [...messages].reverse().find((msg) => msg.sender === 'agent');
            const inProgressTypes = new Set(['analysis_status', 'tool_result']);
            const pendingAt = pendingUserMessageRef.current;
            const lastAgentTs = lastAgentMessage?.createdAt ? new Date(lastAgentMessage.createdAt).getTime() : null;
            const agentAfterPending = pendingAt ? (lastAgentTs !== null && lastAgentTs >= pendingAt) : true;
            const isFinalResponse = lastAgentMessage
                ? !inProgressTypes.has(lastAgentMessage.messageType || '') && agentAfterPending
                : false;
            const timedOut = Date.now() - startedAt > 120000;

            if (isFinalResponse || timedOut) {
                if (isFinalResponse) {
                    pendingUserMessageRef.current = null;
                }
                stopConversationPolling();
                setChatBusy(false);
            }
        };

        poll();
        pollingRef.current = window.setInterval(poll, 2000);
    }, [fetchConversationDetail, stopConversationPolling]);

    const startConversation = async (payload: Record<string, any>) => {
        console.log('[Assistant] Starting conversation with payload', payload);
        setChatBusy(true);
        try {
            const response = await axios.post(
                ENDPOINTS.CONVERSATIONS,
                { ...payload, channel: 'web' },
                { withCredentials: true },
            );

            const conversation = response.data?.data;
            hydrateConversation(conversation, response.data?.initialization);
            message.success('Conversation started');
            console.log('[Assistant] Conversation started', conversation?.id);
            await loadConversations();
            if (conversation?.id) {
                startConversationPolling(conversation.id);
            } else {
                setChatBusy(false);
            }
        } catch (error) {
            console.error('Unable to start conversation', error);
            message.error('Failed to start conversation');
            setChatBusy(false);
        } finally {
            // chatBusy is released by polling completion
        }
    };

    const buildAreaConversationPayload = () => {
        const payload: Record<string, any> = { areaId: selectedAreaId };
        if (areaRange?.[0] && areaRange?.[1]) {
            payload.from = areaRange[0].startOf('day').toISOString();
            payload.to = areaRange[1].endOf('day').toISOString();
        }
        return payload;
    };

    const buildWhatsAppContextPayload = (
        contextType: 'area' | 'measurement' | 'pattern_point',
        contextId?: number | null
    ) => {
        if (!selectedCompanyId || !contextId) {
            return null;
        }
        if (!areaRange?.[0] || !areaRange?.[1]) {
            return null;
        }
        return {
            contextType,
            contextId,
            companyId: selectedCompanyId,
            from: areaRange[0].startOf('day').toISOString(),
            to: areaRange[1].endOf('day').toISOString()
        };
    };

    const startWhatsAppContextChat = async (
        contextType: 'area' | 'measurement' | 'pattern_point',
        contextId?: number | null
    ) => {
        const payload = buildWhatsAppContextPayload(contextType, contextId);
        if (!payload) {
            message.error('Select a company, context, and required analysis range first.');
            return;
        }
        setWhatsAppContextLoading(true);
        try {
            const response = await axios.post(
                ENDPOINTS.WHATSAPP_QR_CONTEXT,
                payload,
                { withCredentials: true }
            );
            const data = response.data?.data || {};
            setWhatsAppContextLink(data.whatsappLink || null);
            setWhatsAppContextMessage(data.message || null);
            setWhatsAppContextExpiresAt(data.expiresAt || null);
            setWhatsAppContextModalOpen(true);
            message.success('WhatsApp context QR generated.');
        } catch (error: any) {
            console.error('Failed to generate WhatsApp context QR', error);
            message.error(error?.response?.data?.message || 'Unable to generate WhatsApp context QR.');
        } finally {
            setWhatsAppContextLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!activeConversationId || !messageInput.trim()) {
            return;
        }
        stopConversationPolling();
        const trimmed = messageInput.trim();
        pendingUserMessageRef.current = Date.now();
        const localMessage: AssistantMessage = {
            id: `local-${Date.now()}`,
            sender: 'user',
            content: trimmed,
            createdAt: new Date().toISOString(),
        };
        setMessagesList((prev) => [...prev, localMessage]);
        setMessageInput('');

        console.log('[Assistant] Sending message', trimmed, 'conversation', activeConversationId);
        setChatBusy(true);
        startConversationPolling(activeConversationId);
        try {
            const response = await axios.post(
                ENDPOINTS.CONVERSATION_MESSAGES(activeConversationId),
                {
                    content: trimmed,
                    sender: 'user',
                    messageType: 'text',
                },
                { withCredentials: true },
            );

            const savedMessage: BackendMessage | undefined = response.data?.data;
            if (savedMessage) {
                setMessagesList((prev) => {
                    const serverMessage = normalizeMessages([savedMessage])[0];
                    const hasLocal = prev.some((msg) => msg.id === localMessage.id);
                    if (hasLocal) {
                        return prev.map((msg) => (msg.id === localMessage.id ? { ...msg, id: serverMessage.id } : msg));
                    }
                    if (prev.some((msg) => msg.id === serverMessage.id)) {
                        return prev;
                    }
                    return sortMessages([...prev, serverMessage]);
                });
            }

            const agentResponse = response.data?.agentResponse;
            if (agentResponse?.content) {
                const agentMessage: AssistantMessage = {
                    id: `agent-${Date.now()}`,
                    sender: 'agent',
                    content: agentResponse.content,
                    messageType: 'analysis',
                    metadata: {
                        attachments: agentResponse.attachments || [],
                    },
                    extraMeta: {
                        tokensUsed: agentResponse.tokensUsed,
                        model: agentResponse.model,
                    },
                };
                setMessagesList((prev) => [...prev, agentMessage]);
                pendingUserMessageRef.current = null;
            }

            if (agentResponse?.followUpQuestion) {
                const followUpMessage: AssistantMessage = {
                    id: `followup-${Date.now()}`,
                    sender: 'agent',
                    content: agentResponse.followUpQuestion,
                    messageType: 'follow_up',
                };
                setMessagesList((prev) => [...prev, followUpMessage]);
            }
            await loadConversations();
        } catch (error) {
            console.error('Unable to send message', error);
            message.error('Message delivery failed');
            stopConversationPolling();
            setChatBusy(false);
        } finally {
            // chatBusy is released by polling completion
        }
    };

    const resumeConversation = async (conversationId: number) => {
        console.log('[Assistant] Resuming conversation', conversationId);
        setChatBusy(true);
        try {
            const response = await axios.get(ENDPOINTS.CONVERSATION_DETAIL(conversationId), {
                withCredentials: true,
            });
            const conversation = response.data?.data;
            if (conversation) {
                hydrateConversation(conversation, null);
                message.success(`Conversation #${conversationId} loaded`);
                console.log('[Assistant] Conversation loaded', conversation);
            }
            await loadConversations();
        } catch (error) {
            console.error('Unable to load conversation', error);
            message.error('Failed to load conversation');
        } finally {
            setChatBusy(false);
        }
    };

    const selectedArea = useMemo(
        () => areas.find((area) => area.id === selectedAreaId) || null,
        [areas, selectedAreaId],
    );

    const selectedAreaGeometry = useMemo<Polygon | MultiPolygon | null>(() => {
        if (!selectedArea?.geom) return null;
        if (selectedArea.geom.type === 'Polygon' || selectedArea.geom.type === 'MultiPolygon') {
            return selectedArea.geom as Polygon | MultiPolygon;
        }
        return null;
    }, [selectedArea]);

    const selectedPattern = useMemo(
        () => patterns.find((pattern) => pattern.id === selectedPatternId) || null,
        [patterns, selectedPatternId],
    );

    const selectedMeasurement = useMemo(
        () => measurements.find((measurement) => measurement.id === selectedMeasurementId) || null,
        [measurements, selectedMeasurementId],
    );

    const measurementPoint = useMemo<Point | null>(() => {
        if (!selectedMeasurement?.geom || selectedMeasurement.geom.type !== 'Point') {
            return null;
        }
        return selectedMeasurement.geom;
    }, [selectedMeasurement]);

    const filteredMeasurementsForPoint = useMemo(() => {
        if (!selectedPatternPointId) {
            return measurements;
        }
        return measurements.filter((measurement) => measurement.patternPointId === selectedPatternPointId);
    }, [measurements, selectedPatternPointId]);

    useEffect(() => {
        fetchMemberships();
    }, [fetchMemberships]);

    useEffect(() => {
        if (selectedCompanyId) {
            loadAreaTypes();
            loadUnassignedMeasurements();
            loadConversations();
        } else {
            setConversationList([]);
        }
    }, [selectedCompanyId, loadAreaTypes, loadUnassignedMeasurements, loadConversations]);

    useEffect(() => {
        if (selectedAreaTypeId) {
            setSelectedAreaId(null);
            setSelectedPatternId(null);
            setSelectedPatternPointId(null);
            setSelectedMeasurementId(null);
            setAreas([]);
            loadAreas(selectedAreaTypeId);
        } else {
            setAreas([]);
        }
    }, [selectedAreaTypeId, loadAreas]);

    useEffect(() => {
        if (selectedAreaId) {
            setSelectedPatternId(null);
            setSelectedPatternPointId(null);
            setSelectedMeasurementId(null);
            setPatterns([]);
            loadPatterns(selectedAreaId);
        } else {
            setPatterns([]);
        }
    }, [selectedAreaId, loadPatterns]);

    useEffect(() => {
        if (selectedPatternId) {
            setSelectedPatternPointId(null);
            setSelectedMeasurementId(null);
            setPatternPoints([]);
            loadPatternPoints(selectedPatternId);
            loadMeasurementsForPattern(selectedPatternId);
        } else {
            setPatternPoints([]);
            setMeasurements([]);
        }
    }, [selectedPatternId, loadPatternPoints, loadMeasurementsForPattern]);

    useEffect(() => {
        // Intentionally do not auto-scroll; preserve user scroll position.
    }, [messagesList]);

    useEffect(() => () => stopConversationPolling(), [stopConversationPolling]);

    const renderAnalysisOptions = (metadata?: any) => {
        if (!metadata?.options) return null;
        return (
            <div className="analysis-options">
                {metadata.options.map((option: any, index: number) => (
                    <div key={option.key || index} className="analysis-option-item">
                        <Text strong>{index + 1}.</Text>
                        <Space direction="vertical" size={0}>
                            <Text strong>{option.description}</Text>
                            <Text type="secondary">{option.key}</Text>
                        </Space>
                    </div>
                ))}
                <Text type="secondary">Reply with the number or key to continue.</Text>
            </div>
        );
    };

    const renderMessage = (messageItem: AssistantMessage) => {
        const isAgent = messageItem.sender === 'agent';
        const attachments = buildAttachmentList(messageItem);
        return (
            <div key={messageItem.id} className={`assistant-message ${isAgent ? 'agent' : 'user'}`}>
                <div className="assistant-message-meta">
                    <Tag color={isAgent ? 'blue' : 'green'} icon={isAgent ? <RobotOutlined /> : <UserOutlined />}>
                        {isAgent ? 'Agent' : 'You'}
                    </Tag>
                    {messageItem.createdAt && (
                        <Text type="secondary" className="assistant-message-time">
                            {formatDate(messageItem.createdAt)}
                        </Text>
                    )}
                </div>
                <div className="assistant-message-body">
                    {messageItem.messageType === 'analysis_options'
                        ? renderAnalysisOptions(messageItem.metadata)
                        : isAgent
                            ? (
                                <div className="markdown-content assistant-markdown">
                                    <ReactMarkdown>{messageItem.content}</ReactMarkdown>
                                </div>
                            )
                            : (
                                <Paragraph className="assistant-message-text">
                                    {messageItem.content}
                                </Paragraph>
                            )}
                    {attachments.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            <Image.PreviewGroup>
                                <Row gutter={[8, 8]}>
                                    {attachments.map((attachment) => (
                                        <Col key={attachment.url} xs={24} sm={12} md={8} lg={6}>
                                            <Image
                                                src={attachment.url}
                                                alt={attachment.label || 'attachment'}
                                                style={{ width: '100%', borderRadius: 6 }}
                                            />
                                            {attachment.label && (
                                                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                                                    {attachment.label}
                                                </Text>
                                            )}
                                        </Col>
                                    ))}
                                </Row>
                            </Image.PreviewGroup>
                        </div>
                    )}
                    {messageItem.extraMeta && (
                        <div className="assistant-message-extra">
                            {messageItem.extraMeta.model && (
                                <Tag icon={<RobotOutlined />} color="purple">
                                    {messageItem.extraMeta.model}
                                </Tag>
                            )}
                            {messageItem.extraMeta.tokensUsed && (
                                <Tag icon={<CommentOutlined />} color="magenta">
                                    {messageItem.extraMeta.tokensUsed} tokens
                                </Tag>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const suggestedQueries = conversationInitialization?.suggestedQueries || [];

    return (
        <div className="assistant-view">
            <div style={{ marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>Assistant</Title>
            </div>
            <Row gutter={16}>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card
                            title="Conversation Context"
                            extra={
                                <Tooltip title="Refresh context data">
                                    <Button
                                        icon={<ReloadOutlined />}
                                        type="text"
                                        onClick={() => {
                                            if (selectedCompanyId) {
                                                loadAreaTypes();
                                                if (selectedAreaTypeId) {
                                                    loadAreas(selectedAreaTypeId);
                                                }
                                                if (selectedAreaId) {
                                                    loadPatterns(selectedAreaId);
                                                }
                                                if (selectedPatternId) {
                                                    loadPatternPoints(selectedPatternId);
                                                    loadMeasurementsForPattern(selectedPatternId);
                                                }
                                            }
                                        }}
                                    />
                                </Tooltip>
                            }
                        >
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <div>
                                    <Text strong>Company</Text>
                                    <Select
                                        value={selectedCompanyId}
                                        onChange={(value) => handleCompanySwitch(value)}
                                        loading={membershipsLoading || companySwitching}
                                        style={{ width: '100%' }}
                                        placeholder="Select company"
                                    >
                                        {memberships.map((membership) => (
                                            <Option key={membership.company.id} value={membership.company.id}>
                                                {membership.company.name} ({membership.role.description})
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <Divider />
                                <div>
                                    <Text strong>Area Type</Text>
                                    <Select
                                        value={selectedAreaTypeId ?? undefined}
                                        onChange={(value) => setSelectedAreaTypeId(value || null)}
                                        loading={contextLoading}
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Pick an area type"
                                    >
                                        {areaTypes.map((type) => (
                                            <Option key={type.id} value={type.id}>
                                                {type.name} {type.isGlobal && <Tag color="gold">Global</Tag>}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <Text strong>Area</Text>
                                    <Select
                                        value={selectedAreaId ?? undefined}
                                        onChange={(value) => setSelectedAreaId(value || null)}
                                        loading={contextLoading}
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Pick an area"
                                        disabled={!selectedAreaTypeId}
                                    >
                                        {areas.map((area) => (
                                            <Option key={area.id} value={area.id}>
                                                {area.name}
                                            </Option>
                                        ))}
                                    </Select>
                                    <div style={{ marginTop: 8 }}>
                                        <Text strong>Analysis range</Text>
                                        <RangePicker
                                            style={{ width: '100%', marginTop: 4 }}
                                            value={areaRange || undefined}
                                            onChange={(range) => setAreaRange(range)}
                                            allowClear
                                            disabled={!selectedAreaId}
                                        />
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<CommentOutlined />}
                                        block
                                        style={{ marginTop: 8 }}
                                        disabled={!selectedAreaId}
                                        loading={chatBusy}
                                        onClick={() =>
                                            selectedAreaId &&
                                            startConversation(buildAreaConversationPayload())
                                        }
                                    >
                                        Start Area Conversation
                                    </Button>
                                    <Tooltip title="Re-analyse from scratch using all historical data, ignoring previous analysis cutoffs.">
                                        <Button
                                            icon={<ReloadOutlined />}
                                            block
                                            style={{ marginTop: 8 }}
                                            disabled={!selectedAreaId}
                                            loading={chatBusy}
                                            onClick={() =>
                                                selectedAreaId &&
                                                startConversation({ ...buildAreaConversationPayload(), forceNew: true })
                                            }
                                        >
                                            Fresh Analysis
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        icon={<CommentOutlined />}
                                        block
                                        style={{ marginTop: 8 }}
                                        disabled={!selectedAreaId || !areaRange?.[0] || !areaRange?.[1]}
                                        loading={whatsAppContextLoading}
                                        onClick={() => startWhatsAppContextChat('area', selectedAreaId)}
                                    >
                                        Chat Through WhatsApp
                                    </Button>
                                </div>
                                <div>
                                    <Text strong>Pattern</Text>
                                    <Select
                                        value={selectedPatternId ?? undefined}
                                        onChange={(value) => setSelectedPatternId(value || null)}
                                        loading={contextLoading}
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Pick a pattern"
                                        disabled={!selectedAreaId}
                                    >
                                        {patterns.map((pattern) => (
                                            <Option key={pattern.id} value={pattern.id}>
                                                {pattern.name} {pattern.patternType && <Tag>{pattern.patternType}</Tag>}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <Button
                                        type="default"
                                        icon={<ApartmentOutlined />}
                                        block
                                        style={{ marginTop: 8 }}
                                        disabled={!selectedPatternId || !areaRange?.[0] || !areaRange?.[1]}
                                        loading={chatBusy}
                                        onClick={() => {
                                            if (selectedPatternId && areaRange?.[0] && areaRange?.[1]) {
                                                startConversation({
                                                    patternId: selectedPatternId,
                                                    from: areaRange[0].startOf('day').toISOString(),
                                                    to: areaRange[1].endOf('day').toISOString(),
                                                });
                                            }
                                        }}
                                    >
                                        Analyse Pattern Trends
                                    </Button>
                                </div>
                                <div>
                                    <Text strong>Pattern Point</Text>
                                    <Select
                                        value={selectedPatternPointId ?? undefined}
                                        onChange={(value) => setSelectedPatternPointId(value || null)}
                                        loading={contextLoading}
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Pick a pattern point"
                                        disabled={!selectedPatternId}
                                    >
                                        {patternPoints.map((point) => (
                                            <Option key={point.id} value={point.id}>
                                                #{point.pointIndex + 1} &middot; {point.label}
                                            </Option>
                                        ))}
                                    </Select>
                                    <Button
                                        type="primary"
                                        icon={<PlayCircleOutlined />}
                                        block
                                        style={{ marginTop: 8 }}
                                        disabled={!selectedPatternPointId}
                                        loading={chatBusy}
                                        onClick={() =>
                                            selectedPatternPointId &&
                                            startConversation({ patternPointId: selectedPatternPointId })
                                        }
                                    >
                                        Start Pattern Point Conversation
                                    </Button>
                                    <Button
                                        icon={<CommentOutlined />}
                                        block
                                        style={{ marginTop: 8 }}
                                        disabled={!selectedPatternPointId || !areaRange?.[0] || !areaRange?.[1]}
                                        loading={whatsAppContextLoading}
                                        onClick={() => startWhatsAppContextChat('pattern_point', selectedPatternPointId)}
                                    >
                                        Chat Through WhatsApp
                                    </Button>
                                </div>
                                <Divider />
                                <div>
                                    <Text strong>Measurements for selected pattern</Text>
                                    <Select
                                        value={selectedMeasurementId ?? undefined}
                                        onChange={(value) => setSelectedMeasurementId(value || null)}
                                        loading={measurementsLoading}
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder="Pick a measurement"
                                        disabled={!selectedPatternId || filteredMeasurementsForPoint.length === 0}
                                    >
                                        {filteredMeasurementsForPoint.map((measurement) => (
                                            <Option key={measurement.id} value={measurement.id}>
                                                {measurement.measurementCode} — {measurement.measuredAt
                                                    ? formatMeasurementTimestamp(measurement.measuredAt, measurement)
                                                    : formatUtcTimestamp(measurement.createdAt)}
                                            </Option>
                                        ))}
                                    </Select>
                                    <Button
                                        block
                                        style={{ marginTop: 8 }}
                                        icon={<CommentOutlined />}
                                        disabled={!selectedMeasurementId}
                                        loading={chatBusy}
                                        onClick={() =>
                                            selectedMeasurementId &&
                                            startConversation({ measurementId: selectedMeasurementId })
                                        }
                                    >
                                        Start Measurement Conversation
                                    </Button>
                                    <Button
                                        block
                                        style={{ marginTop: 8 }}
                                        icon={<CommentOutlined />}
                                        disabled={!selectedMeasurementId || !areaRange?.[0] || !areaRange?.[1]}
                                        loading={whatsAppContextLoading}
                                        onClick={() => startWhatsAppContextChat('measurement', selectedMeasurementId)}
                                    >
                                        Chat Through WhatsApp
                                    </Button>
                                </div>
                            </Space>
                        </Card>

                        <Card title="Spatial preview & measurement details">
                            {selectedAreaGeometry ? (
                                <PatternPointMapCard
                                    areaName={selectedArea?.name}
                                    areaGeometry={selectedAreaGeometry}
                                    patternCoordSystem={selectedPattern?.coordSystem || undefined}
                                    patternPoints={patternPoints}
                                    selectedPatternPointId={selectedPatternPointId}
                                    measurementPoint={measurementPoint || undefined}
                                />
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Select an area to visualize the pattern and measurement."
                                />
                            )}
                            <Divider />
                            {selectedMeasurement ? (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Descriptions
                                        colon={false}
                                        size="small"
                                        column={1}
                                        labelStyle={{ width: 160 }}
                                        className="measurement-meta"
                                    >
                                        <Descriptions.Item label="Measurement">
                                            {selectedMeasurement.measurementCode}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Measured">
                                            {selectedMeasurement.measuredAt
                                                ? formatMeasurementTimestamp(selectedMeasurement.measuredAt, selectedMeasurement)
                                                : formatUtcTimestamp(selectedMeasurement.createdAt)}
                                        </Descriptions.Item>
                                        {selectedMeasurement.device && (
                                            <Descriptions.Item label="Device">
                                                <Space split={<Divider type="vertical" />}>
                                                    <span>{selectedMeasurement.device.label}</span>
                                                    {selectedMeasurement.device.serialNumber && (
                                                        <Text type="secondary">
                                                            SN: {selectedMeasurement.device.serialNumber}
                                                        </Text>
                                                    )}
                                                    {selectedMeasurement.device.codePrefix && (
                                                        <Text type="secondary">
                                                            Prefix: {selectedMeasurement.device.codePrefix}
                                                        </Text>
                                                    )}
                                                </Space>
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                    <div>
                                        <Text strong>Measurement values</Text>
                                        {selectedMeasurement.values && selectedMeasurement.values.length > 0 ? (
                                            <List
                                                size="small"
                                                dataSource={selectedMeasurement.values}
                                                renderItem={(value) => (
                                                    <List.Item>
                                                        <Space
                                                            split={<Divider type="vertical" />}
                                                            className="measurement-value-row"
                                                        >
                                                            <Text>{value.valueType}</Text>
                                                            <Text strong>{formatMeasurementValue(value) || 'N/A'}</Text>
                                                        </Space>
                                                    </List.Item>
                                                )}
                                            />
                                        ) : (
                                            <Text type="secondary">This measurement has no recorded values.</Text>
                                        )}
                                    </div>
                                    <div>
                                        <Text strong>Files</Text>
                                        {selectedMeasurement.files && selectedMeasurement.files.length > 0 ? (
                                            <List
                                                size="small"
                                                dataSource={selectedMeasurement.files}
                                                renderItem={(file) => (
                                                    <List.Item>
                                                        <Space
                                                            split={<Divider type="vertical" />}
                                                            className="measurement-file-row"
                                                        >
                                                            <a
                                                                href={file.signedUrl || '#'}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {file.fileName}
                                                            </a>
                                                            {file.mimeType && (
                                                                <Text type="secondary">{file.mimeType}</Text>
                                                            )}
                                                            {file.fileSize && (
                                                                <Text type="secondary">
                                                                    {formatFileSize(file.fileSize)}
                                                                </Text>
                                                            )}
                                                        </Space>
                                                    </List.Item>
                                                )}
                                            />
                                        ) : (
                                            <Text type="secondary">No files associated with this measurement.</Text>
                                        )}
                                    </div>
                                </Space>
                            ) : (
                                <Text type="secondary">
                                    Select a measurement to view its values, device details, and attachments.
                                </Text>
                            )}
                        </Card>

                        <Card
                            title="Measurements linked to selection"
                            extra={<Tag icon={<ApartmentOutlined />}>Pattern scoped</Tag>}
                        >
                            {measurementsLoading && <Spin />}
                            {!measurementsLoading && filteredMeasurementsForPoint.length === 0 && (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a pattern to view data" />
                            )}
                            {!measurementsLoading && filteredMeasurementsForPoint.length > 0 && (
                                <List
                                    dataSource={filteredMeasurementsForPoint}
                                    renderItem={(item) => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="start"
                                                    type="link"
                                                    icon={<MessageOutlined />}
                                                    onClick={() => startConversation({ measurementId: item.id })}
                                                >
                                                    Discuss
                                                </Button>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={`${item.measurementCode} — ${item.measuredAt
                                                    ? formatMeasurementTimestamp(item.measuredAt, item)
                                                    : formatUtcTimestamp(item.createdAt)}`}
                                                description={
                                                    <Space split={<Divider type="vertical" />}>
                                                        <span>
                                                            NDVI:{' '}
                                                            <Text strong>
                                                                {getValueFromMeasurement(item, 'NDVI') ?? 'N/A'}
                                                            </Text>
                                                        </span>
                                                        <span>
                                                            Coverage:{' '}
                                                            <Text strong>
                                                                {getValueFromMeasurement(item, 'Coverage') ?? 'N/A'}
                                                            </Text>
                                                        </span>
                                                        <span>
                                                            Stress:{' '}
                                                            <Text strong>
                                                                {getValueFromMeasurement(item, 'Stressed') ?? 'N/A'}
                                                            </Text>
                                                        </span>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>

                        <Card
                            title="Unassigned measurements"
                            extra={<Tag color="orange">No pattern point</Tag>}
                        >
                            {unassignedLoading && <Spin />}
                            {!unassignedLoading && unassignedMeasurements.length === 0 && (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No unassigned measurements" />
                            )}
                            {!unassignedLoading && unassignedMeasurements.length > 0 && (
                                <List
                                    size="small"
                                    dataSource={unassignedMeasurements}
                                    renderItem={(item) => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="start"
                                                    size="small"
                                                    icon={<PlayCircleOutlined />}
                                                    onClick={() => startConversation({ measurementId: item.id })}
                                                >
                                                    Open conversation
                                                </Button>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={`${item.measurementCode}`}
                                                description={item.measuredAt
                                                    ? `Measured ${formatMeasurementTimestamp(item.measuredAt, item)}`
                                                    : `Uploaded ${formatUtcTimestamp(item.createdAt)}`}
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>

                        <Card
                            title="Recent Conversations"
                            extra={
                                <Button
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    onClick={loadConversations}
                                    loading={conversationsLoading}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            {conversationsLoading && <Spin />}
                            {!conversationsLoading && conversationList.length === 0 && (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No conversations yet" />
                            )}
                            {!conversationsLoading && conversationList.length > 0 && (
                                <List
                                    size="small"
                                    dataSource={conversationList}
                                    renderItem={(item) => (
                                        <List.Item
                                            actions={[
                                                <Button
                                                    key="resume"
                                                    size="small"
                                                    icon={<MessageOutlined />}
                                                    onClick={() => resumeConversation(item.id)}
                                                >
                                                    Resume
                                                </Button>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                title={
                                                    <Space>
                                                        <span>Conversation #{item.id}</span>
                                                        <Tag color={item.status === 'active' ? 'green' : 'default'}>
                                                            {item.status}
                                                        </Tag>
                                                    </Space>
                                                }
                                                description={
                                                    <Space direction="vertical" size={0}>
                                                        {item.patternPoint?.name && (
                                                            <Text>Pattern point: {item.patternPoint.name}</Text>
                                                        )}
                                                        {item.measurementId && (
                                                            <Text>Measurement #{item.measurementId}</Text>
                                                        )}
                                                        {item.area?.name && <Text>Area: {item.area.name}</Text>}
                                                        <Text type="secondary">
                                                            Updated {formatDate(item.updatedAt)}
                                                        </Text>
                                                    </Space>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Card>
                    </Space>
                </Col>

                <Col xs={24} lg={16}>
                    <Card
                        title={
                            <Space>
                                <RobotOutlined />
                                <span>Assistant Conversation</span>
                            </Space>
                        }
                        extra={
                            activeConversationId ? (
                                <Tag color="green" icon={<SolutionOutlined />}>
                                    Conversation #{activeConversationId}
                                </Tag>
                            ) : (
                                <Tag icon={<AimOutlined />}>Choose context to begin</Tag>
                            )
                        }
                    >
                        <div className="assistant-chat-window">
                            {messagesList.length === 0 ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Start a conversation to see the transcript"
                                />
                            ) : (
                                messagesList.map((messageEntry) => renderMessage(messageEntry))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {suggestedQueries.length > 0 && (
                            <div className="assistant-suggestions">
                                <Text strong>Suggested Questions:</Text>
                                <Space wrap style={{ marginTop: 8 }}>
                                    {suggestedQueries.map((query) => (
                                        <Button
                                            key={query}
                                            size="small"
                                            onClick={() => setMessageInput(query)}
                                        >
                                            {query}
                                        </Button>
                                    ))}
                                </Space>
                            </div>
                        )}

                        <Divider />
                        <Space.Compact className="assistant-input-row">
                            <Input.TextArea
                                value={messageInput}
                                onChange={(event) => setMessageInput(event.target.value)}
                                placeholder={
                                    activeConversationId
                                        ? 'Type your reply'
                                        : 'Select a pattern point or measurement to start chatting'
                                }
                                autoSize={{ minRows: 2, maxRows: 4 }}
                                onPressEnter={(event) => {
                                    if (!event.shiftKey) {
                                        event.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                disabled={!activeConversationId}
                            />
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={handleSendMessage}
                                disabled={!activeConversationId || !messageInput.trim()}
                                loading={chatBusy}
                            >
                                Send
                            </Button>
                        </Space.Compact>
                    </Card>
                </Col>
            </Row>
            <Modal
                title="Chat Through WhatsApp"
                open={whatsAppContextModalOpen}
                onCancel={() => setWhatsAppContextModalOpen(false)}
                footer={null}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Text>
                        Scan this QR code and send the generated message in WhatsApp to start analysis with this context.
                    </Text>
                    {whatsAppContextLink && (
                        <div style={{ textAlign: 'center' }}>
                            <QRCodeSVG value={whatsAppContextLink} size={200} level="M" includeMargin />
                        </div>
                    )}
                    {whatsAppContextMessage && (
                        <Paragraph copyable={{ text: whatsAppContextMessage }}>
                            <Text strong>Message code:</Text> {whatsAppContextMessage}
                        </Paragraph>
                    )}
                    {whatsAppContextLink && (
                        <Space wrap>
                            <Button type="primary" href={whatsAppContextLink} target="_blank" rel="noreferrer">
                                Open WhatsApp
                            </Button>
                            <Button
                                onClick={() => {
                                    if (whatsAppContextLink) {
                                        navigator.clipboard?.writeText(whatsAppContextLink);
                                        message.success('WhatsApp link copied.');
                                    }
                                }}
                            >
                                Copy Link
                            </Button>
                        </Space>
                    )}
                    {whatsAppContextExpiresAt && (
                        <Text type="secondary">Expires: {formatDate(whatsAppContextExpiresAt)}</Text>
                    )}
                </Space>
            </Modal>
        </div>
    );
};

export default ConversationalAssistant;
